"use client";
import { useAuth } from "@/contexts/AuthContext";
import { HeaderProvider } from "@/contexts/HeaderContext";
import { Link, usePathname } from "@/i18n/navigation";
import { getRouteDetails } from "@/utils/routeHelper";
import clsx from "clsx";
import { memo } from "react";
import HeaderActions from "./HeaderActions";
import LanguageAndCurrencySwitcher from "./LanguageAndCurrencySwitcher";
import LoginRegister from "./LoginRegister";
import MenuButton from "./MenuButton";
import ProductNavigationDropdown from "./ProductNavigationDropdown";
import ProductNavigationTabs from "./ProductNavigationTabs";
import ReservationControl from "./ReservationControl";
import TurnaHeaderLogo from "./TurnaHeaderLogo";
import { Modal } from "./ui/Modal";
import Share from "./ui/Share";

const HeaderContent = memo(function HeaderContent() {
  const { session, hideLoginModal } = useAuth();
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  const {
    isBusResultPage,
    isBusReservation,
    isFerryResultPage,
    isHotelResultPage,
    isCarResultPage,
    isAnyProductHome,
    isLanding,
    isFlightReservation,
    isFlightResultPage,
    shouldBlurHeader,
    isAnyReservationPage,
    isAnyResultPage,
    isPayment,
  } = routeDetails;
  const isMetaSearchFlightPage = isFlightReservation || isFlightResultPage;
  const showIconOnlyOnMobile = isAnyResultPage || isAnyReservationPage || isPayment;
  const renderHeader = () => {
    // Flight header
    return (
      <header
        className={clsx(
          "md:border-primary-weak fixed top-0 left-0 z-20 w-full border-b md:rounded-none md:bg-white md:shadow-none md:backdrop-blur-none",
          shouldBlurHeader
            ? "rounded-b-3xl border-white bg-white/60 shadow-[0_8px_42px_0_rgba(0,0,0,0.10)] backdrop-blur-xl"
            : "border-primary-weak bg-white",
        )}
      >
        <div
          className={clsx(
            "flex items-center py-3 md:gap-6 md:p-6",
            "px-4",
            isBusReservation || isPayment
              ? "justify-between pr-3 pl-4 md:justify-start"
              : "gap-3",
          )}
        >
          <MenuButton />
          <Link href="/" className="flex items-center">
            {isMetaSearchFlightPage ? (
              <>
                {/* MetaSearch: full logo always */}
                <span className="metasearch-only">
                  <TurnaHeaderLogo iconOnly={false} />
                </span>
                {/* Non-MetaSearch: responsive logo */}
                <span className="metasearch-hide md:hidden">
                  <TurnaHeaderLogo iconOnly={true} />
                </span>
                <span className="metasearch-hide hidden md:inline">
                  <TurnaHeaderLogo iconOnly={false} />
                </span>
              </>
            ) : showIconOnlyOnMobile ? (
              <>
                <span className="md:hidden">
                  <TurnaHeaderLogo iconOnly={true} />
                </span>
                <span className="hidden md:inline">
                  <TurnaHeaderLogo iconOnly={false} />
                </span>
              </>
            ) : (
              <TurnaHeaderLogo iconOnly={false} />
            )}
          </Link>
          {isLanding ? (
            <ProductNavigationDropdown />
          ) : isMetaSearchFlightPage ? (
            <span className="metasearch-only">
              <ProductNavigationDropdown />
            </span>
          ) : null}

          {isMetaSearchFlightPage && session?.IsMetaSearch ? null : (
            <div id="header-middle-portal" className="min-w-0 flex-1"></div>
          )}

          {(isBusResultPage ||
            isFerryResultPage ||
            isCarResultPage ||
            isHotelResultPage) ? (
            <>
              <div className="flex flex-shrink-0 items-center gap-3 md:hidden">
                <Share
                  buttonClassName="flex items-center justify-center h-6 w-6 rounded-full hover:bg-gray-100 transition-colors"
                  translationNamespace="common"
                />
                <HeaderActions className="hidden md:flex" />
              </div>
              <div className="hidden md:block">
                <HeaderActions />
              </div>
            </>
          ) : (
            <HeaderActions />
          )}
        </div>
        {isAnyProductHome && (
          <div className="flex justify-between gap-4 border-t border-black/5 px-[21px] py-3 md:hidden">
            <ProductNavigationTabs />
          </div>
        )}
        {session?.IsMetaSearch && isFlightResultPage && (
          <div
            id="header-middle-portal"
            className="border-primary-weak min-w-0 flex-1 border-t px-4 py-3 md:hidden md:border-none"
          ></div>
        )}
        <div id="header-bottom-portal" className="md:hidden"></div>
        {session?.IsMetaSearch && isFlightReservation && (
          <div
            id="header-middle-portal"
            className="min-w-0 flex-1 px-4 pt-1.5 pb-3 md:hidden"
          ></div>
        )}
      </header>
    );
  };

  return (
    <>
      {renderHeader()}

      <Modal id="login-modal" onClose={() => hideLoginModal()}>
        <LoginRegister />
      </Modal>
      <Modal id="language-and-currency-modal">
        <LanguageAndCurrencySwitcher />
      </Modal>
      <Modal id="reservation-control-modal">
        <ReservationControl />
      </Modal>
    </>
  );
});

export default function Header({ token }) {
  return (
    <HeaderProvider>
      <HeaderContent token={token} />
    </HeaderProvider>
  );
}
